import { Box } from '@chakra-ui/react';
import { useIsMobile, useMelioIntl } from '@melio/ar-domain';
import { Button, Group } from '@melio/penny';
import { forwardRef } from '@melio/platform-utils';

import { GuestPaymentLayout, GuestPaymentLayoutProps } from '../../../layout';
import {
  PaymentDetailsSection,
  PaymentDetailsSectionProps,
  PaymentSourceSection,
  PaymentSourceSectionProps,
} from '../components';

export type ReviewAndConfirmScreenProps = PaymentSourceSectionProps &
  PaymentDetailsSectionProps &
  Pick<GuestPaymentLayoutProps, 'isLoading' | 'onBack'> & {
    isSaving?: boolean;
    onSubmit: VoidFunction;
    isDisabled?: boolean;
  };

export const ReviewAndConfirmScreen = forwardRef<ReviewAndConfirmScreenProps>(
  (
    {
      bill,
      vendor,
      fundingSource,
      scheduleDate,
      onSubmit,
      isSaving,
      isDisabled,
      onEditFundingSource,
      onEditScheduleDate,
      ...props
    },
    ref
  ) => {
    const { formatMessage } = useMelioIntl();
    const paddingX = useIsMobile() ? 'm' : 'l';
    return (
      <GuestPaymentLayout
        data-component={ReviewAndConfirmScreen.displayName}
        billId={bill?.id}
        hasPadding={false}
        isDisabled={isDisabled || isSaving}
        headerTextVariant="review-and-confirm"
        data-testid="review-and-confirm-screen"
        {...props}
        ref={ref}
      >
        {props.isLoading ? null : (
          <Group variant="vertical" spacing="l">
            <Group hasDivider variant="vertical" spacing="m">
              <PaymentDetailsSection bill={bill} vendor={vendor} paddingX={paddingX} />
              <PaymentSourceSection
                paddingX={paddingX}
                fundingSource={fundingSource}
                scheduleDate={scheduleDate}
                onEditFundingSource={onEditFundingSource}
                onEditScheduleDate={onEditScheduleDate}
                isDisabled={isDisabled || isSaving}
              >
                <Button
                  isFullWidth
                  label={formatMessage('ar.guestPayment.activities.paymentReview.confirm.button.label')}
                  isLoading={isSaving}
                  onClick={onSubmit}
                  isDisabled={isDisabled}
                  data-testid="review-and-confirm-submit-button"
                />
              </PaymentSourceSection>
            </Group>
          </Group>
        )}
      </GuestPaymentLayout>
    );
  }
);
ReviewAndConfirmScreen.displayName = 'ReviewAndConfirmScreen';

const Container = forwardRef((props, ref) => (
  <Box data-component="Container" paddingX={useIsMobile() ? 'm' : 'l'} {...props} ref={ref} />
));
Container.displayName = 'Container';
